import type { MiharuConfig } from '@/types/config'
import type { InstanceContext, MiharuInstance } from '@/types/core'
import { MiharuError } from '@/types/core'
import { MiharuInstanceImpl } from './instance'
import { Logger } from '@/utils/logger'

const logger = new Logger('InstanceManager')

export class MiharuInstanceManager {
  private static instances = new Map<string, MiharuInstance>()
  private static contexts = new Map<string, InstanceContext>()

  static create(
    config: MiharuConfig,
    context?: InstanceContext
  ): MiharuInstance {
    const instanceId = config.instanceId || this.generateContextualId(context)

    if (this.instances.has(instanceId)) {
      const existing = this.instances.get(instanceId)!
      logger.warn(`Instance ${instanceId} already exists, returning existing`)
      return existing
    }

    // Validate instance doesn't conflict with existing ones
    this.validateInstanceConfig(instanceId, config, context)

    const instance = new MiharuInstanceImpl(instanceId, config, context)
    this.instances.set(instanceId, instance)

    if (context) {
      this.contexts.set(instanceId, context)
    }

    logger.info(`Created miharu instance: ${instanceId}`, { context })
    return instance
  }

  private static generateContextualId(context?: InstanceContext): string {
    if (!context) {
      return `miharu-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    }

    const components = [
      context.service || 'service',
      context.environment || 'env',
      context.team || 'team',
    ]

    return `miharu-${components.join('-')}-${Date.now().toString(36)}`
  }

  private static validateInstanceConfig(
    _instanceId: string,
    config: MiharuConfig,
    _context?: InstanceContext
  ): void {
    // Check for storage conflicts
    for (const [existingId, existingInstance] of this.instances) {
      if (existingInstance.config.adapter === config.adapter) {
        // SQLite files should be unique per instance
        if (config.adapter === 'sqlite') {
          const existingFile = (existingInstance.config.options as any).filename
          const newFile = (config.options as any).filename
          if (existingFile === newFile) {
            throw new MiharuError(
              `SQLite file conflict: ${newFile} already in use by instance ${existingId}`,
              'INSTANCE_STORAGE_CONFLICT'
            )
          }
        }
      }
    }
  }

  static getByContext(
    contextFilter: Partial<InstanceContext>
  ): MiharuInstance[] {
    const matches: MiharuInstance[] = []

    for (const [instanceId, context] of this.contexts) {
      const matchesFilter = Object.entries(contextFilter).every(
        ([key, value]) => context[key as keyof InstanceContext] === value
      )

      if (matchesFilter) {
        const instance = this.instances.get(instanceId)
        if (instance) matches.push(instance)
      }
    }

    return matches
  }

  static async shutdownByContext(
    contextFilter: Partial<InstanceContext>
  ): Promise<void> {
    const instances = this.getByContext(contextFilter)
    await Promise.all(instances.map((instance) => instance.shutdown()))
  }

  static getInstance(instanceId: string): MiharuInstance | undefined {
    return this.instances.get(instanceId)
  }

  static getAllInstances(): MiharuInstance[] {
    return Array.from(this.instances.values())
  }

  static async shutdownAll(): Promise<void> {
    const shutdownPromises = Array.from(this.instances.values()).map(
      instance => instance.shutdown()
    )
    
    await Promise.all(shutdownPromises)
    
    this.instances.clear()
    this.contexts.clear()
    
    logger.info('All instances shut down')
  }

  static removeInstance(instanceId: string): boolean {
    const removed = this.instances.delete(instanceId)
    this.contexts.delete(instanceId)
    
    if (removed) {
      logger.info(`Instance ${instanceId} removed`)
    }
    
    return removed
  }

  static getInstanceStats(): {
    totalInstances: number
    instancesByAdapter: Record<string, number>
    instancesByEnvironment: Record<string, number>
  } {
    const instances = Array.from(this.instances.values())
    const contexts = Array.from(this.contexts.values())
    
    return {
      totalInstances: instances.length,
      instancesByAdapter: instances.reduce((acc, instance) => {
        const adapter = instance.config.adapter
        acc[adapter] = (acc[adapter] || 0) + 1
        return acc
      }, {} as Record<string, number>),
      instancesByEnvironment: contexts.reduce((acc, context) => {
        const env = context.environment
        acc[env] = (acc[env] || 0) + 1
        return acc
      }, {} as Record<string, number>)
    }
  }
}